import { useTheme } from '@/contexts/ThemeContext';

const footerLinks = {
  'Soluções': [
    'Sistemas Web',
    'Aplicativos Mobile',
    'ERP para Escolas',
    'ERP para Reciclagem',
    'ERP Corporativo',
    'Integrações & APIs',
  ],
  'Empresa': [
    'Sobre a Granato',
    'Nossa Metodologia',
    'Cases de Sucesso',
    'Blog Técnico',
    'Carreiras',
  ],
  'Suporte': [
    'Central de Ajuda',
    'Documentação',
    'Status dos Sistemas',
    'Política de Privacidade',
    'Termos de Uso',
  ],
};

export default function Footer() {
  const { isDark } = useTheme();

  return (
    <footer className={`transition-colors duration-500 ${isDark ? 'bg-[#040810]' : 'bg-slate-100'} border-t ${isDark ? 'border-white/8' : 'border-slate-200'}`}>
      {/* CTA Banner */}
      <div className={`border-b ${isDark ? 'border-white/8' : 'border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="flex flex-col gap-2 text-center lg:text-left">
            <h3 className={`text-2xl lg:text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Pronto para transformar a gestão da sua empresa?
            </h3>
            <p className={`text-base ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              Fale com nossos especialistas e receba um diagnóstico gratuito do seu projeto.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <a
              href="#contato"
              onClick={(e) => { e.preventDefault(); document.querySelector('#contato')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg text-white font-semibold text-sm whitespace-nowrap cursor-pointer transition-all duration-200 hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}
            >
              <i className="ri-rocket-line"></i>
              Solicitar Projeto
            </a>
            <a
              href="https://wa.me/5500000000000"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className={`inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg font-semibold text-sm border whitespace-nowrap cursor-pointer transition-all duration-200 ${
                isDark
                  ? 'text-white border-white/15 bg-white/5 hover:bg-white/10'
                  : 'text-slate-700 border-slate-300 bg-white hover:bg-slate-50'
              }`}
            >
              <i className="ri-whatsapp-line text-green-500"></i>
              WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="flex items-center gap-3">
              <div style={{ borderRadius: '12px', overflow: 'hidden', width: 40, height: 40, flexShrink: 0 }}>
                <img
                  src="https://storage.readdy-site.link/project_files/4a2f3690-6afc-4752-8f11-296a8cd10aa8/9df4885e-fe7a-4292-927f-532659452029_Gemini_Generated_Image_e0eqt8e0eqt8e0eq.png?v=c10a7e5f8467af600c66ebccd92d7add"
                  alt="Granato Tecnologia"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <div className="flex flex-col" style={{ lineHeight: 1.15 }}>
                <span className={`font-bold text-lg tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>Granato</span>
                <span className="text-cyan-500 font-bold text-base tracking-tight">Tecnologia</span>
              </div>
            </div>
            <p className={`text-sm leading-relaxed max-w-xs ${isDark ? 'text-slate-500' : 'text-slate-600'}`}>
              Especialistas em desenvolvimento de sistemas web, aplicativos mobile e ERPs sob medida para empresas que buscam eficiência, controle e crescimento.
            </p>
            <div className="flex items-center gap-3">
              {['ri-linkedin-box-line', 'ri-instagram-line', 'ri-github-line', 'ri-youtube-line'].map((icon) => (
                <a
                  key={icon}
                  href="#"
                  rel="nofollow"
                  className={`w-9 h-9 flex items-center justify-center rounded-lg border transition-all duration-200 cursor-pointer ${
                    isDark
                      ? 'border-white/10 bg-white/5 text-slate-400 hover:text-white hover:border-white/20'
                      : 'border-slate-300 bg-white text-slate-500 hover:text-slate-900 hover:border-slate-400'
                  }`}
                >
                  <i className={`${icon} text-base`}></i>
                </a>
              ))}
            </div>
            <div className={`flex flex-col gap-2 text-sm ${isDark ? 'text-slate-500' : 'text-slate-600'}`}>
              <div className="flex items-center gap-2">
                <i className="ri-map-pin-line text-cyan-500 text-base"></i>
                <span>Brasil — Atendimento Nacional</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-mail-line text-cyan-500 text-base"></i>
                <span>contato@granatotecnologia.com.br</span>
              </div>
            </div>
          </div>

          {/* Links */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category} className="flex flex-col gap-5">
              <h4 className={`text-sm font-bold tracking-wide uppercase ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <a href="#" className="hover:text-cyan-500 transition-colors">{category}</a>
              </h4>
              <ul className="flex flex-col gap-3">
                {links.map((link) => (
                  <li key={link}>
                    <a
                      href="#"
                      rel="nofollow"
                      className={`text-sm transition-colors cursor-pointer ${
                        isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className={`border-t ${isDark ? 'border-white/8' : 'border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className={`text-sm text-center sm:text-left ${isDark ? 'text-slate-600' : 'text-slate-500'}`}>
            &copy; {new Date().getFullYear()} Granato Tecnologia. Todos os direitos reservados.
          </p>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            <span className={`text-xs ${isDark ? 'text-slate-600' : 'text-slate-500'}`}>Todos os sistemas operacionais</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
